import { Fragment } from "react";
import { RotateCwIcon } from "lucide-react";
import { useCurrentRefinements } from "react-instantsearch";

import { cn } from "@tudigo-monorepo/core-tudigo-theme";
import { useTranslation } from "@tudigo-monorepo/core-tudigo-translations";
import {
  ButtonRebrand,
  DropdownMenu,
} from "@tudigo-monorepo/web-tudigo-components";

import { useCanRefineList } from "../../hooks/use-can-refine-list";
import {
  algoliaRefinementAttributes,
  type RefinementAttributeList,
} from "../../models/algolia-refinement-attributes";
import { useSearchProjectsContext } from "../../state/search-projects.context";
import { CustomClearRefinements } from "./custom-clear-refinements";
import {
  CustomRefinementList,
  type RefinementListItem,
} from "./custom-refinement-list";
import { RefinementListContent } from "./refinement-list-content";
import { RefinementListTrigger } from "./refinement-list-trigger";

export type SearchProjectsRefinementsProps = {
  className?: string;
  onRefineItemChange?: (
    refinementList: RefinementAttributeList,
    item: RefinementListItem,
    checked: boolean,
  ) => void;
};

export function SearchProjectsRefinements(
  props: SearchProjectsRefinementsProps,
) {
  const { refinements } = useSearchProjectsContext();
  const { canRefine: hasRefinements } = useCurrentRefinements();
  const canRefineList = useCanRefineList();
  const { t } = useTranslation();

  const availableRefinementLists = Object.entries(algoliaRefinementAttributes)
    .map(([key, value]) => ({ key, value }) as RefinementAttributeList)
    .filter(({ key }) => refinements?.includes(key));

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center gap-y-6 md:flex-row md:flex-wrap md:gap-x-4 md:gap-y-2",
        props.className,
      )}
    >
      {availableRefinementLists.map((refinementList, index) => {
        return (
          <Fragment key={refinementList.key}>
            {/* Desktop version */}
            <div className="hidden md:block">
              <DropdownMenu>
                <RefinementListTrigger refinementList={refinementList} />
                <RefinementListContent
                  refinementList={refinementList}
                  onRefineItemChange={props.onRefineItemChange}
                />
              </DropdownMenu>
            </div>
            {/* Mobile version */}
            {canRefineList(refinementList.key) && (
              <div className="flex w-full flex-col gap-y-2 md:hidden">
                <p className="font-roobert text-sm font-medium">
                  {t(
                    `search_projects_page.refinements.${refinementList.value}.label`,
                  )}
                </p>
                <CustomRefinementList
                  attribute={algoliaRefinementAttributes[refinementList.key]}
                  refinementList={refinementList}
                  className="shadow-card rounded bg-white p-6"
                  onRefineItemChange={props.onRefineItemChange}
                />
              </div>
            )}
          </Fragment>
        );
      })}

      {hasRefinements && (
        <ButtonRebrand
          asChild
          variant="secondary"
          className="hidden p-0 transition-none hover:bg-inherit focus-visible:ring-0 md:flex"
        >
          <CustomClearRefinements className="h-10 p-3">
            <RotateCwIcon className="h-4 w-4 text-neutral-700" />
            <p className="font-sans text-sm text-neutral-700">
              {t(`search_projects_page.refinements.reset.cta.label`)}
            </p>
          </CustomClearRefinements>
        </ButtonRebrand>
      )}
    </div>
  );
}
